import type { BoxProps, StackProps, TypographyProps } from "@mui/material";
import { Box } from "@mui/material";
import ExpandCollapseButton from "@entrilia/shared/components/ExpandCollapseButton.tsx";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon.tsx";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon.tsx";
import type { TabularDataCell } from "../../../../../../../../api/types/svTypes.ts";
import GrandTotalCell from "../../common/GrandTotalCell.tsx";
import { hasAvailableDrillDown } from "../../common/hasAvailableDrillDown.ts";
import DimensionCellDisplayValue from "./dimension-column/DimensionCellDisplayValue.tsx";
import { MeasureCell as MeasureCellComponent } from "./measure-column/MeasureCell.tsx";

interface BuildDimensionCellOptions {
  cellType: "dimension";
  cell: TabularDataCell | undefined;
  formattedValue?: string;
  typographyProps?: TypographyProps;
  containerProps?: StackProps;
}

interface BuildMeasureCellOptions {
  cellType: "measure";
  cell: TabularDataCell | undefined;
  formattedValue: string;
  aggregation?: boolean;
  isGrandTotal?: boolean;
  disableCellClick?: boolean;
}

interface GroupingToggleProps {
  expanded: boolean;
  onToggle: () => void;
}

interface BuildGroupCellOptions {
  cellType: "group";
  cell: TabularDataCell | undefined;
  formattedValue: string;
  typographyProps?: TypographyProps;
  containerProps?: BoxProps;
  showGroupingToggle?: boolean;
  groupingToggleProps?: GroupingToggleProps;
}

interface BuildGroupLeafCellOptions {
  cellType: "groupLeaf";
  cell: TabularDataCell | undefined;
  formattedValue?: string;
  typographyProps?: TypographyProps;
  containerProps?: StackProps;
}

export type BuildTabularCellOptions =
  BuildDimensionCellOptions | BuildMeasureCellOptions | BuildGroupCellOptions | BuildGroupLeafCellOptions;

export interface MeasureCellDisplayModel {
  disableCellClick: boolean;
  formattedValue: string;
  isGrandTotal: boolean;
  isInteractive: boolean;
  variant: "body1" | "subtitle2";
}

export const getMeasureCellDisplayModel = ({
  cell,
  formattedValue,
  aggregation,
  isGrandTotal,
  disableCellClick,
}: BuildMeasureCellOptions): MeasureCellDisplayModel => {
  const isInteractive = hasAvailableDrillDown(cell?.drillDownId);

  return {
    disableCellClick: !!disableCellClick,
    formattedValue,
    isGrandTotal: isGrandTotal === true,
    isInteractive,
    variant: aggregation ? "subtitle2" : "body1",
  };
};

export const buildTabularCell = (options: BuildTabularCellOptions) => {
  if (options.cellType === "dimension") {
    return (
      <DimensionCellDisplayValue
        cell={options.cell}
        formattedValue={options.formattedValue}
        typographyProps={options.typographyProps}
        containerProps={options.containerProps}
      />
    );
  }

  if (options.cellType === "group") {
    return (
      <Box
        {...options.containerProps}
        sx={[
          { display: "flex", alignItems: "center" },
          ...(Array.isArray(options.containerProps?.sx) ? options.containerProps.sx : [options.containerProps?.sx]),
        ]}
      >
        {options.showGroupingToggle && options.groupingToggleProps ? (
          <Box className="MuiDataGrid-groupingCriteriaCellToggle">
            <ExpandCollapseButton
              expanded={options.groupingToggleProps.expanded}
              onToggle={options.groupingToggleProps.onToggle}
              tabIndex={-1}
              ExpandIcon={ExpandIcon}
              CollapseIcon={CollapseIcon}
            />
          </Box>
        ) : null}
        <DimensionCellDisplayValue
          cell={options.cell}
          formattedValue={options.formattedValue}
          typographyProps={options.typographyProps}
        />
      </Box>
    );
  }

  if (options.cellType === "groupLeaf") {
    return (
      <DimensionCellDisplayValue
        cell={options.cell}
        formattedValue={options.formattedValue}
        typographyProps={options.typographyProps}
        containerProps={options.containerProps ?? { sx: { ml: "46px" } }}
      />
    );
  }

  const displayModel = getMeasureCellDisplayModel(options);

  if (displayModel.isGrandTotal) {
    return (
      <GrandTotalCell
        formattedValue={displayModel.formattedValue}
        disableCellClick={displayModel.disableCellClick || !displayModel.isInteractive}
      />
    );
  }

  const interactive = !displayModel.disableCellClick && displayModel.isInteractive;
  return (
    <MeasureCellComponent
      variant={displayModel.variant}
      value={displayModel.formattedValue}
      sx={{ ...(interactive && { cursor: "pointer" }), "&:hover": interactive ? { textDecoration: "underline" } : {} }}
    />
  );
};
